/**
 * InvestmentDecisionDialog Component
 *
 * Modal dialog for recording investment decisions.
 * Opens when DecisionMaker selects a restricted status (approved, deferred, rejected, cancelled).
 * Captures rationale and optional reconsider date, then creates decision record.
 */

import { useCreateDecision } from '@medsuite/api-client';
import { InvestmentStatusLabel, InvestmentStatusToDecisionStatus } from '@medsuite/models';
import { Button, TextArea, Alert, DatePicker } from '@medsuite/ui-components';
import { useState } from 'react';

import { useAuth } from '../../../providers/AuthProvider';

import type { InvestmentStatus, InvestmentDecisionStatus } from '@medsuite/models';

interface InvestmentDecisionDialogProps {
  investmentId: number;
  investmentTitle: string;
  currentStatus: InvestmentStatus;
  targetStatus: InvestmentStatus;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Restricted decision status type (only statuses that go through this dialog)
 */
type RestrictedDecisionStatus = 'A' | 'D' | 'R' | 'C';

/**
 * Status labels for decision display
 */
const DECISION_STATUS_LABELS: Record<RestrictedDecisionStatus, string> = {
  A: 'Approved',
  D: 'Deferred',
  R: 'Rejected',
  C: 'Cancelled',
};

/**
 * Badge classes for decision statuses with proper contrast
 * Using explicit classes for Tailwind purge compatibility
 */
const DECISION_BADGE_CLASSES: Record<RestrictedDecisionStatus, string> = {
  A: 'bg-green-100 text-green-900 dark:bg-green-800 dark:text-white',
  D: 'bg-yellow-100 text-yellow-900 dark:bg-yellow-700 dark:text-white',
  R: 'bg-red-100 text-red-900 dark:bg-red-800 dark:text-white',
  C: 'bg-gray-100 text-gray-900 dark:bg-gray-600 dark:text-white',
};

/**
 * Badge classes for investment statuses with proper contrast
 */
const STATUS_BADGE_CLASSES: Record<InvestmentStatus, string> = {
  new: 'bg-gray-100 text-gray-900 dark:bg-gray-600 dark:text-white',
  submitted: 'bg-blue-100 text-blue-900 dark:bg-blue-800 dark:text-white',
  approved: 'bg-green-100 text-green-900 dark:bg-green-800 dark:text-white',
  deferred: 'bg-yellow-100 text-yellow-900 dark:bg-yellow-700 dark:text-white',
  rejected: 'bg-red-100 text-red-900 dark:bg-red-800 dark:text-white',
  planned: 'bg-purple-100 text-purple-900 dark:bg-purple-800 dark:text-white',
  in_progress: 'bg-indigo-100 text-indigo-900 dark:bg-indigo-800 dark:text-white',
  complete: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-800 dark:text-white',
  cancelled: 'bg-gray-100 text-gray-900 dark:bg-gray-600 dark:text-white',
};

export default function InvestmentDecisionDialog({
  investmentId,
  investmentTitle,
  currentStatus,
  targetStatus,
  isOpen,
  onClose,
  onSuccess,
}: InvestmentDecisionDialogProps) {
  const { user } = useAuth();
  const createDecision = useCreateDecision(investmentId);

  // Local state
  const [rationale, setRationale] = useState('');
  const [reconsiderDate, setReconsiderDate] = useState<string>('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Get decision status code from target status
  const decisionStatus = InvestmentStatusToDecisionStatus[targetStatus] as InvestmentDecisionStatus;

  // Check if reconsider date should be shown (for D/R/C decisions)
  const showReconsiderDate = decisionStatus && decisionStatus !== 'A';

  // Reset form
  const resetForm = () => {
    setRationale('');
    setReconsiderDate('');
    setShowConfirmation(false);
  };

  // Handle dialog close
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Handle decision submission
  const handleSubmit = async () => {
    if (!showConfirmation) {
      setShowConfirmation(true);
      return;
    }

    try {
      await createDecision.mutateAsync({
        investment_status: decisionStatus,
        decision_rationale: rationale.trim() || null,
        reconsider_date: reconsiderDate || null,
      });

      if (onSuccess) {
        onSuccess();
      }
      handleClose();
    } catch (error) {
      console.error('Failed to create decision:', error);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (showConfirmation) {
      setShowConfirmation(false);
    } else {
      handleClose();
    }
  };

  if (!isOpen || !decisionStatus) {
    return null;
  }

  // Cast to restricted type since this dialog is only used for A/D/R/C statuses
  const restrictedStatus = decisionStatus as RestrictedDecisionStatus;
  const decisionLabel = DECISION_STATUS_LABELS[restrictedStatus];
  const decisionBadgeClasses = DECISION_BADGE_CLASSES[restrictedStatus];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleCancel}
      />

      {/* Dialog */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {showConfirmation ? 'Confirm Decision' : 'Record Decision'}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{investmentTitle}</p>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {!showConfirmation ? (
              <div className="space-y-4">
                {/* Status Change Summary */}
                <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      From:
                    </span>
                    <div className="mt-1">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${STATUS_BADGE_CLASSES[currentStatus]}`}
                      >
                        {InvestmentStatusLabel[currentStatus]}
                      </span>
                    </div>
                  </div>
                  <svg
                    className="h-6 w-6 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                  <div className="text-right">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      To:
                    </span>
                    <div className="mt-1">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${decisionBadgeClasses}`}
                      >
                        {decisionLabel}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Decision Maker Info (read-only) */}
                <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                    Decision Maker
                  </h4>
                  <div className="space-y-1">
                    <p className="text-sm text-blue-900 dark:text-blue-100">
                      <span className="font-medium">Name:</span> {user?.name || 'Unknown'}
                    </p>
                    <p className="text-sm text-blue-900 dark:text-blue-100">
                      <span className="font-medium">Email:</span> {user?.username || 'Unknown'}
                    </p>
                    <p className="text-sm text-blue-900 dark:text-blue-100">
                      <span className="font-medium">Date:</span> {new Date().toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Decision Rationale */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Decision Rationale
                    <span className="text-gray-400 text-xs ml-1">(Optional)</span>
                  </label>
                  <TextArea
                    value={rationale}
                    onChange={(e) => setRationale(e.target.value)}
                    placeholder="Provide reasoning for this decision..."
                    rows={4}
                    className="w-full"
                  />
                </div>

                {/* Reconsider Date (for D/R/C) */}
                {showReconsiderDate && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Reconsider Date
                      <span className="text-gray-400 text-xs ml-1">(Optional)</span>
                    </label>
                    <DatePicker
                      value={reconsiderDate}
                      onChange={(e) => setReconsiderDate(e.target.value)}
                      className="w-full"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Date to reconsider this investment
                    </p>
                  </div>
                )}

                {/* Status-specific alerts */}
                {decisionStatus === 'A' && (
                  <Alert variant="success">
                    This investment will be marked as <strong>Approved</strong> and can proceed to
                    planning.
                  </Alert>
                )}
                {decisionStatus === 'D' && (
                  <Alert variant="warning">
                    This investment will be marked as <strong>Deferred</strong> for later
                    reconsideration.
                  </Alert>
                )}
                {decisionStatus === 'R' && (
                  <Alert variant="error">
                    This investment will be marked as <strong>Rejected</strong>. It can be
                    re-submitted for review later.
                  </Alert>
                )}
                {decisionStatus === 'C' && (
                  <Alert variant="error">
                    <strong>Warning:</strong> Cancelling this investment is a terminal state.
                  </Alert>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {/* Confirmation Summary */}
                <Alert variant="info">Please confirm your decision.</Alert>

                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Decision:
                    </span>
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${decisionBadgeClasses}`}
                    >
                      {decisionLabel}
                    </span>
                  </div>

                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Decision Maker:
                    </span>
                    <p className="text-sm text-gray-900 dark:text-gray-100">{user?.name}</p>
                  </div>

                  {rationale && (
                    <div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Rationale:
                      </span>
                      <p className="text-sm text-gray-900 dark:text-gray-100 mt-1 whitespace-pre-wrap">
                        {rationale}
                      </p>
                    </div>
                  )}

                  {reconsiderDate && (
                    <div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Reconsider Date:
                      </span>
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        {new Date(reconsiderDate).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Are you sure you want to record this decision?
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 flex justify-end gap-3 rounded-b-lg">
            <Button variant="secondary" onClick={handleCancel}>
              {showConfirmation ? 'Back' : 'Cancel'}
            </Button>
            <Button variant="primary" onClick={handleSubmit} disabled={createDecision.isPending}>
              {createDecision.isPending
                ? 'Recording...'
                : showConfirmation
                  ? 'Confirm Decision'
                  : 'Continue'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
